import { useState, useEffect } from "react";
import { useDealer } from "./useDealer";
import { useSafeApiCall } from "./api";
import { useApiClient } from "./api/useApiClient";
import type {
  ApplicationCondition,
  ConditionSection,
} from "src/types/conditions";
import { deriveConditionSection } from "src/utils/conditionUtils";

interface UseConditionsReturn {
  conditions: ApplicationCondition[];
  loading: boolean;
  error: Error | null;
}

interface ConditionsQueryParams {
  application_id?: string;
  status?: string[];
  tag?: string[];
  sort?: string;
  order?: string;
}

/**
 * Hook to fetch conditions for an application from the backend.
 *
 * Conditions are fetched from the `/dealer-portal/v1/{dealer_id}/conditions` endpoint
 * and automatically transformed to include UI section derivation and DigiFi sync state.
 *
 * @param applicationId - The application ID to fetch conditions for
 * @param queryParams - Optional query parameters to filter conditions (status, tag, sort, order)
 * @param enabled - Whether to enable/disable the fetch (useful for conditional fetching)
 * @returns Object containing conditions array, loading state, and any error
 *
 * @example
 * const { conditions, loading, error } = useConditions(appId, { status: ['pending'] }, true);
 */
export const useConditions = (
  applicationId: string,
  queryParams?: Partial<ConditionsQueryParams>,
  enabled: boolean = true,
): UseConditionsReturn => {
  const { dealer_id } = useDealer();
  const { fetchFromApi } = useApiClient();
  const [conditions, setConditions] = useState<ApplicationCondition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const safeApiCall = useSafeApiCall();

  useEffect(() => {
    if (!enabled || !dealer_id || !applicationId) {
      setConditions([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Build query params matching admin portal pattern
    const queryParamsToSend: Record<
      string,
      string | number | boolean | string[]
    > = {
      application_id: applicationId,
    };

    if (queryParams?.status) {
      queryParamsToSend.status = queryParams.status;
    }
    if (queryParams?.tag) {
      queryParamsToSend.tag = queryParams.tag;
    }
    if (queryParams?.sort) {
      queryParamsToSend.sort = queryParams.sort;
    }
    if (queryParams?.order) {
      queryParamsToSend.order = queryParams.order;
    }

    safeApiCall(() =>
      fetchFromApi(`/dealer-portal/v1/${dealer_id}/conditions`, {
        queryParams: queryParamsToSend,
      }),
    )
      .then((response) => {
        if (response) {
          // Transform response[] to ApplicationCondition[]
          const transformedConditions = (
            response as Array<Record<string, unknown>>
          ).map(
            (condition): ApplicationCondition =>
              ({
                ...condition,
                application_id: applicationId,
                section:
                  (condition.section as ConditionSection | undefined) ||
                  deriveConditionSection(condition),
                digifi_synced: condition.digifi_synced ?? false,
              }) as ApplicationCondition,
          );

          setConditions(transformedConditions);
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setLoading(false);
      });
  }, [applicationId, dealer_id, queryParams, enabled]);

  return { conditions, loading, error };
};
